// Movement helpers — pure functions that compute "what moved this week"
// derivations from the latest-week `signals` rows. Tolerant of both:
//   - camelCase normalised rows from /api/signals/history
//   - PascalCase raw v4 rows (used by unit tests)
// and accept either pipe strings ("a | b | c") or already-split arrays.

const num = (v) => (typeof v === "number" ? v : 0);

const splitPipe = (v) => {
    if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
    if (typeof v === "string") return v.split("|").map((x) => x.trim()).filter(Boolean);
    return [];
};

export const pipeFirstN = (v, n = 5) => splitPipe(v).slice(0, n);

// Pick the first non-empty value across alias keys.
const pick = (s, ...keys) => {
    if (!s) return undefined;
    for (const k of keys) {
        const v = s[k];
        if (v !== undefined && v !== null && v !== "") return v;
    }
    return undefined;
};

const visibilityOf = (s) => String(pick(s, "visibility", "SignalVisibility") || "").toUpperCase();
const trackIdOf = (s) => pick(s, "trackID", "TrackID", "trackId") || null;
const signalIdOf = (s) => pick(s, "signalID", "SignalID", "signalId") || null;
const signalNameOf = (s) => pick(s, "signalName", "SignalName") || "—";
const containerOf = (s) => pick(s, "container", "Container") || "";
const regulatorySplitOf = (s) => String(pick(s, "regulatorySplit", "RegulatorySplit") || "").toUpperCase();
const isRegulatoryFlag = (s) => {
    const v = pick(s, "isRegulatory", "IsRegulatory");
    if (v === true) return true;
    return String(v || "").toUpperCase() === "TRUE";
};
const scoreDeltaOf = (s) => num(pick(s, "signalScoreDelta", "SignalScoreDelta", "delta"));
const companyDeltaOf = (s) => num(pick(s, "companyDelta", "UniqueCompaniesDelta"));
const companyTypeDeltaOf = (s) => num(pick(s, "companyTypeDelta", "UniqueCompanyTypesDelta"));
const articleDeltaOf = (s) => num(pick(s, "articleDelta", "ArticleCountDelta"));
const momentumOf = (s) => pick(s, "momentum", "MomentumLabel") || null;
const narrativeOf = (s) => pick(s, "weeklyComparisonNarrative", "WeeklyComparisonNarrative") || null;
const evolutionOf = (s) => pick(s, "evolution", "EvolutionSummary") || null;
const newCompaniesOf = (s) => pick(s, "newCompanies", "NewCompanies");
const droppedCompaniesOf = (s) => pick(s, "droppedCompanies", "DroppedCompanies");
const newCompanyTypesOf = (s) => pick(s, "newCompanyTypes", "NewCompanyTypes");
const newAssetClassesOf = (s) => pick(s, "newAssetClasses", "NewAssetClasses");
const newBlockchainsOf = (s) => pick(s, "newBlockchains", "NewBlockchains");
const companyTypeShiftOf = (s) => pick(s, "companyTypeShift", "CompanyTypeShift") || null;

export const isVisibleEnough = (s) => {
    const v = visibilityOf(s);
    return v === "SIGNAL" || v === "WATCHLIST";
};

const REG_HINTS = ["regulator", "regulatory", "compliance", "license", "licens", "rule", "framework"];
const isRegulatory = (s) => {
    if (regulatorySplitOf(s) === "REGULATORY") return true;
    if (isRegulatoryFlag(s)) return true;
    const text = `${signalNameOf(s)} ${containerOf(s)}`.toLowerCase();
    return REG_HINTS.some((h) => text.includes(h));
};

// (helper removed — narratives now use the signal name verbatim to preserve casing)

const card = (kind, signal, headline, metric, narrative) => ({
    kind,
    headline,
    metric,
    narrative,
    trackId: trackIdOf(signal),
    signalID: signalIdOf(signal),
    signalName: signalNameOf(signal),
});

/** Build 4–6 compact movement cards from the latest week's signal rows. */
export const computeMovementCards = (latestSignals) => {
    if (!Array.isArray(latestSignals) || latestSignals.length === 0) return [];
    const visible = latestSignals.filter(isVisibleEnough);
    const pool = visible.length ? visible : latestSignals;

    const out = [];
    const used = new Set();
    const take = (s) => { used.add(signalIdOf(s)); };
    const isUsed = (s) => used.has(signalIdOf(s));

    // 1. Strongest strengthening signal — biggest positive score delta
    const strongest = [...pool]
        .filter((s) => scoreDeltaOf(s) > 0)
        .sort((a, b) => scoreDeltaOf(b) - scoreDeltaOf(a))[0];
    if (strongest) {
        out.push(card("strengthening", strongest,
            "Top strengthening",
            `score +${scoreDeltaOf(strongest)}`,
            `${signalNameOf(strongest)} strengthened. Score +${scoreDeltaOf(strongest)} this week.`));
        take(strongest);
    }

    // 2. Most softening — most negative
    const softening = [...pool]
        .filter((s) => scoreDeltaOf(s) < 0 && !isUsed(s))
        .sort((a, b) => scoreDeltaOf(a) - scoreDeltaOf(b))[0];
    if (softening) {
        out.push(card("softening", softening,
            "Top softening",
            `score ${scoreDeltaOf(softening)}`,
            `${signalNameOf(softening)} softened this week. Score ${scoreDeltaOf(softening)}.`));
        take(softening);
    }

    // 3. Biggest company-breadth expansion
    const breadth = [...pool]
        .filter((s) => companyDeltaOf(s) > 0 && !isUsed(s))
        .sort((a, b) => companyDeltaOf(b) - companyDeltaOf(a))[0];
    if (breadth) {
        out.push(card("breadth", breadth,
            "Widest actor breadth",
            `+${companyDeltaOf(breadth)} companies`,
            `${signalNameOf(breadth)} broadened across ${companyDeltaOf(breadth)} more companies.`));
        take(breadth);
    }

    // 4. New actor types
    const actorTypes = [...pool]
        .filter((s) => !isUsed(s) && (companyTypeDeltaOf(s) > 0 || splitPipe(newCompanyTypesOf(s)).length > 0))
        .sort((a, b) => companyTypeDeltaOf(b) - companyTypeDeltaOf(a))[0];
    if (actorTypes) {
        const newTypes = pipeFirstN(newCompanyTypesOf(actorTypes), 3);
        out.push(card("actor-types", actorTypes,
            "New actor groups",
            companyTypeDeltaOf(actorTypes) > 0 ? `+${companyTypeDeltaOf(actorTypes)} types` : `${newTypes.length} new`,
            newTypes.length ? `New actor groups: ${newTypes.join(", ")}.` : `${signalNameOf(actorTypes)} attracted new actor groups.`));
        take(actorTypes);
    }

    // 5. New exposure (asset class or blockchain)
    const exposure = [...pool]
        .filter((s) => !isUsed(s) && (splitPipe(newAssetClassesOf(s)).length > 0 || splitPipe(newBlockchainsOf(s)).length > 0))[0];
    if (exposure) {
        const ac = pipeFirstN(newAssetClassesOf(exposure), 3);
        const bc = pipeFirstN(newBlockchainsOf(exposure), 3);
        let line;
        if (ac.length && bc.length) {
            line = `New exposure: ${ac.join(", ")} · ${bc.join(", ")}.`;
        } else if (ac.length) {
            line = `New asset classes: ${ac.join(", ")}.`;
        } else {
            line = `New blockchains: ${bc.join(", ")}.`;
        }
        out.push(card("exposure", exposure,
            "New exposure",
            ac.length ? `+${ac.length} class${ac.length > 1 ? "es" : ""}` : `+${bc.length} chain${bc.length > 1 ? "s" : ""}`,
            line));
        take(exposure);
    }

    // 6. Regulatory movement
    const regs = pool.filter((s) => isRegulatory(s) && !isUsed(s));
    const reg = regs.sort((a, b) => scoreDeltaOf(b) - scoreDeltaOf(a))[0];
    if (reg) {
        const sd = scoreDeltaOf(reg);
        const direction = sd > 0 ? "strengthened" : (sd < 0 ? "softened" : "held steady");
        out.push(card("regulatory", reg,
            "Regulatory movement",
            sd ? (sd > 0 ? `score +${sd}` : `score ${sd}`) : (momentumOf(reg) || "STABLE"),
            `${signalNameOf(reg)} ${direction} this week.`));
        take(reg);
    }

    return out.slice(0, 6);
};

/** Normalise a free-text signal name for fuzzy matching: lowercase, strip
 *  inline punctuation between alphanumerics (so `U.S.` collapses to `us`,
 *  `don't` to `dont`), then convert remaining punctuation to spaces and
 *  collapse whitespace. Used as the final fallback when no direct ID or
 *  exact-name lookup succeeds, so a briefing reference like
 *  `"U.S. Regulators Open New Trading Rails"` still resolves to a track
 *  whose `StableTrackName` is `"US Regulators Open New Trading Rails"`. */
const normaliseName = (s) => {
    if (typeof s !== "string") return "";
    return s.toLowerCase()
        .replace(/[\u2018\u2019\u201c\u201d]/g, "'")
        // Collapse periods/apostrophes between word chars: U.S. → us
        .replace(/(?<=[a-z0-9])[.'`]+(?=[a-z0-9])/g, "")
        .replace(/[^a-z0-9\s]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
};

/** Map briefing topSignals/regulatoryWatch to chips that can link to the
 *  current Market Signal board. Returns [{signalID, trackId, name, kind}].
 *
 *  Matching priority — falls through until a track is found:
 *    1. Explicit `TrackID` / `trackId` / `CanonicalTrackID` on the briefing item.
 *    2. `SignalID` → latest-week `tracksByTrackId` via `latestSignalsByPipeId`.
 *    3. Exact (case-insensitive) match against `StableTrackName`,
 *       `SuggestedDisplayLabel`, alias fields, and `LatestWeeklyFraming`
 *       on each track.
 *    4. Exact match against any historical `WeeklyFraming` title in
 *       `historyByTrackId` (every weekly framing the track has ever
 *       carried — so a paraphrased briefing title that re-uses an older
 *       weekly framing still links).
 *    5. Normalised match (`normaliseName`) against the same combined
 *       index. Unmatched references stay disabled.
 *
 *  We deliberately do NOT do token / Jaccard fuzzy matching: it produces
 *  false-positives on related-but-distinct framings (e.g. "Open Trading
 *  Rails" vs "Open Crypto Derivatives Trading"). Per the spec we only
 *  link when there is an unambiguous textual match somewhere in the
 *  registry. */
export const briefingReferencedItems = (briefing, latestSignalsByPipeId, tracksByTrackId, historyByTrackId) => {
    if (!briefing) return [];
    const out = [];
    const seen = new Set();
    const tracksList = tracksByTrackId ? Object.values(tracksByTrackId) : [];

    // Pre-build exact + normalised lookup indexes so we don't re-scan per item.
    const exactLookup = new Map();
    const normLookup = new Map();

    const addAlias = (value, trackId) => {
        if (typeof value !== "string" || !value.trim() || !trackId) return;
        const exact = value.trim().toLowerCase();
        if (!exactLookup.has(exact)) exactLookup.set(exact, trackId);
        const norm = normaliseName(value);
        if (norm && !normLookup.has(norm)) normLookup.set(norm, trackId);
    };

    // (a) Track-level alias fields.
    const aliasKeys = [
        "StableTrackName", "SuggestedDisplayLabel", "DisplayName", "TrackName",
        "Title", "ShortName", "LatestWeeklyFraming",
    ];
    for (const t of tracksList) {
        if (!t) continue;
        for (const k of aliasKeys) addAlias(t[k], t.TrackID);
        const alts = t.AlternateNames || t.AliasNames || t.HistoricalNames;
        if (Array.isArray(alts)) for (const v of alts) addAlias(v, t.TrackID);
        // `WeeklyFramingsJSON` (if upstream ships it) — array of weekly framing rows.
        const wfJson = t.WeeklyFramingsJSON;
        if (Array.isArray(wfJson)) {
            for (const wf of wfJson) {
                if (!wf) continue;
                addAlias(typeof wf === "string" ? wf : (wf.title || wf.framing || wf.WeeklyFraming || wf.headline), t.TrackID);
            }
        }
    }

    // (b) Per-track historical weekly framings from /api/tracks/history.
    if (historyByTrackId && typeof historyByTrackId === "object") {
        for (const [tid, weeks] of Object.entries(historyByTrackId)) {
            if (!Array.isArray(weeks)) continue;
            for (const w of weeks) {
                if (!w) continue;
                addAlias(w.WeeklyFraming || w.weeklyFraming || w.SignalName || w.signalName || w.BriefingHeadline || w.weeklyHeadline || w.title || w.headline, tid);
            }
        }
    }

    const resolve = (item, kind) => {
        if (!item) return;
        const sid = (item.SignalID || item.signalID || item.signalId || "").trim();
        const sname = item.SignalName || item.signalName || item.name || item.title || null;
        const explicitTrackId = (item.TrackID || item.trackId || item.CanonicalTrackID || item.canonicalTrackId || "").trim() || null;
        let trackId = null;

        // 1. Explicit trackId on the briefing item.
        if (explicitTrackId && tracksByTrackId && tracksByTrackId[explicitTrackId]) {
            trackId = explicitTrackId;
        }
        // 2. SignalID → track via the latest-week pipe index.
        if (!trackId && sid && latestSignalsByPipeId && latestSignalsByPipeId.has(sid)) {
            trackId = trackIdOf(latestSignalsByPipeId.get(sid));
        }
        // 3. Exact (case-insensitive trim) name lookup.
        if (!trackId && typeof sname === "string" && sname.trim()) {
            const exact = sname.trim().toLowerCase();
            if (exactLookup.has(exact)) trackId = exactLookup.get(exact);
        }
        // 4. Normalised lookup (punctuation/whitespace tolerant).
        if (!trackId && typeof sname === "string" && sname.trim()) {
            const norm = normaliseName(sname);
            if (norm && normLookup.has(norm)) trackId = normLookup.get(norm);
        }

        const key = sid || (sname && sname.toLowerCase()) || trackId;
        if (!key || seen.has(key)) return;
        seen.add(key);
        out.push({
            signalID: sid || null,
            trackId,
            name: sname || (trackId && tracksByTrackId && tracksByTrackId[trackId] && tracksByTrackId[trackId].StableTrackName) || sid || "—",
            kind,
        });
    };

    (briefing.topSignals || briefing.TopSignalsJSON || []).forEach((it) => resolve(it, "top"));
    (briefing.regulatoryWatch || briefing.RegulatoryWatchJSON || []).forEach((it) => resolve(it, "regulatory"));

    return out;
};

/** Stakeholder-implication helpers used by the Client Relevance demo.
 *  Tolerates both camelCase (normalised) and PascalCase (raw) signals. */
const STAKEHOLDER_KEYS_BY_KIND = {
    bank: ["implicationForBanks", "ImplicationForBanks"],
    asset_manager: ["implicationForAssetManagers", "ImplicationForAssetManagers"],
    exchange: ["implicationForExchanges", "ImplicationForExchanges"],
    tokenization_platform: ["implicationForTokenizationPlatforms", "ImplicationForTokenizationPlatforms"],
    stablecoin_issuer: ["implicationForStablecoinIssuers", "ImplicationForStablecoinIssuers"],
    infra_provider: ["implicationForInfraProviders", "ImplicationForInfraProviders"],
};

export const implicationForKind = (signal, kind = "bank") => {
    if (!signal) return null;
    const keys = STAKEHOLDER_KEYS_BY_KIND[kind] || STAKEHOLDER_KEYS_BY_KIND.bank;
    return pick(signal, ...keys) || null;
};

// Internal accessors exported for sibling components (e.g. LatestMovement)
// that need the same dual-casing rules without re-implementing them.
export const _accessors = {
    pick,
    splitPipe,
    trackIdOf,
    signalIdOf,
    signalNameOf,
    scoreDeltaOf,
    companyDeltaOf,
    companyTypeDeltaOf,
    articleDeltaOf,
    momentumOf,
    narrativeOf,
    evolutionOf,
    newCompaniesOf,
    droppedCompaniesOf,
    newCompanyTypesOf,
    newAssetClassesOf,
    newBlockchainsOf,
    companyTypeShiftOf,
};
